/* eslint-disable prettier/prettier */
import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Fab from '@material-ui/core/Fab';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import AddIcon from '@material-ui/icons/Add';
import BarChartIcon from '@material-ui/icons/BarChart';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import StopIcon from '@material-ui/icons/Stop';
import ShareIcon from '@material-ui/icons/Share';
import ReceiptIcon from '@material-ui/icons/Receipt';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import MaterialGrid from '@material-ui/core/Grid';
import { isMobile } from 'react-device-detect';
import GeneralGrid from 'common/components/layout/grid/GeneralGrid';
import Table from 'common/components/table/tableGeneral';
import { ActionNameElement, ColumnsBelowSearch } from 'common/components/table/tableComponents';
import { appColors } from 'styles/colors';
import VendasOnlineParceiroModal from './components/VendasOnlineParceiroModal';
import PrimaryButton from 'common/components/buttons/primaryButton';
import axios from 'axios';
import { BASE_API, USER_TOKEN, BASE_S3 } from 'config/consts';
import ShareModal from '../tanqueCheio/components/ShareModal';
import ResultsModal from './components/ResultsModal';
import { getFormValues, initialize } from 'redux-form';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import LoadingProgress from 'common/components/progress/loading';
import { getList, getDetail, dispatchFormReset, getPartners } from './vendasOnlineParceiroActions';
import { getList as getLottiesList } from '../../lotties/lottiesActions';
import { VENDAS_ONLINE_PARCEIRO_TYPE_ID } from './types';
import {
  CHILD_ACTION_EDIT_BLOCKED_MESSAGE,
  isMultiStationChildAction,
} from '../helpers/clubsMultiStation';

function Main(props) {
  const [open, setOpen] = useState(false);
  const [tablePage, setTablePage] = useState(1);
  const [shownPerPage, setShownPerPage] = useState(10);
  const [nameSearched, setNameSearched] = useState('');
  const [value, setValue] = useState(0);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [resultsModalOpen, setResultsModalOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [shareActivity, setShareActivity] = useState(null);
  const loadedActivityIdRef = React.useRef(null);

  const stationId = props.auth.user.establishments[0].stations[0].id;

  // Função para buscar atividades - definida primeiro para ser usada em outros callbacks
  const fetchActivities = useCallback(async () => {
    if (!stationId) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const token = JSON.parse(localStorage.getItem(USER_TOKEN));
      
      const response = await axios.get(
        `${BASE_API}/actions-list-table/?where[station_id]=${stationId}&where[type_id]=${VENDAS_ONLINE_PARCEIRO_TYPE_ID}&where[active]=1&where[is_draft]=0`,
        {
          headers: { Authorization: `${token}` },
          timeout: 30000,
        }
      );

      const activities = response.data?.data || response.data || [];
      
      // Garantir que activities é um array
      if (!Array.isArray(activities)) {
        console.warn('activities não é um array:', activities);
        setData([]);
        setTotalRecords(0);
        return;
      }
      
      const formattedData = activities.map((activity) => {
        let parsedBody = {};
        const rawConfig = activity.station_specific_data || activity.body;
        if (rawConfig) {
          try {
            parsedBody = typeof rawConfig === 'string' ? JSON.parse(rawConfig) : rawConfig;
          } catch (e) {
            console.warn('Erro ao parsear station_specific_data:', e);
          }
        }

        return {
          id: activity.id,
          name: activity.name || parsedBody.name || 'Sem nome',
          status: activity.active ? 'Ativa' : 'Inativa',
          createdAt: new Date(activity.createdAt).toLocaleDateString('pt-BR'),
          participations: activity.participations_count || 0,
          revenue: activity.total_revenue || 0,
          image: activity.image ? `${BASE_S3}/${activity.image}` : null,
          parent_action_id: activity.parent_action_id || null,
          rawData: activity,
        };
      });

      setData(formattedData);
      setTotalRecords(response.data.total || formattedData.length);
    } catch (error) {
      console.error('Erro ao buscar atividades:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [stationId, tablePage, shownPerPage]);

  const handleOpen = useCallback(() => {
    setSelectedActivity(null);
    setValue(0);
    props.dispatchFormReset();
    setOpen(true);
  }, [props]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleEditActivity = useCallback(async (activity) => {
    if (isMultiStationChildAction(activity) || isMultiStationChildAction(activity?.rawData)) {
      alert(CHILD_ACTION_EDIT_BLOCKED_MESSAGE);
      return;
    }
    try {
      const token = JSON.parse(localStorage.getItem(USER_TOKEN));
      const response = await axios.get(`${BASE_API}/actions/${activity.id}`, {
        headers: { Authorization: `${token}` },
      });
      
      const activityData = response.data;
      if (isMultiStationChildAction(activityData)) {
        alert(CHILD_ACTION_EDIT_BLOCKED_MESSAGE);
        return;
      }
      
      let parsedBody = {};
      const rawEditConfig = activityData.station_specific_data || activityData.body;
      if (rawEditConfig) {
        try {
          parsedBody = typeof rawEditConfig === 'string' ? JSON.parse(rawEditConfig) : rawEditConfig;
        } catch (e) {
          console.warn('Erro ao fazer parse do station_specific_data:', e);
        }
      }

      // Formatar categorias para o formato esperado pelo SelectMultipleV4
      // As categorias vêm em category_actions (relação many-to-many)
      let rawCategories = [];
      if (activityData.category_actions && Array.isArray(activityData.category_actions) && activityData.category_actions.length > 0) {
        // category_actions contém objetos com category dentro
        rawCategories = activityData.category_actions.map(ca => ca.category || ca);
      } else if (activityData.categories && Array.isArray(activityData.categories) && activityData.categories.length > 0) {
        rawCategories = activityData.categories;
      } else if (parsedBody.categories_field && Array.isArray(parsedBody.categories_field) && parsedBody.categories_field.length > 0) {
        rawCategories = parsedBody.categories_field;
      } else if (parsedBody.categories && Array.isArray(parsedBody.categories) && parsedBody.categories.length > 0) {
        rawCategories = parsedBody.categories;
      }
      
      const categoriesField = rawCategories.map(cat => {
        if (typeof cat === 'object' && cat !== null) {
          return { id: cat.id, title: cat.title || cat.name || '' };
        }
        return { id: cat, title: '' };
      });

      // Formatar stations para o formato esperado pelo SelectMultiple
      // Buscar todas as stations do usuário para enriquecer com o nome
      const userStations = [];
      const establishments = props.auth?.user?.establishments || [];
      establishments.forEach(est => {
        if (est.stations && Array.isArray(est.stations)) {
          est.stations.forEach(s => {
            if (!userStations.find(us => us.id === s.id)) {
              userStations.push(s);
            }
          });
        }
      });
      
      let rawStations = parsedBody.stations_field || [];
      if (rawStations.length === 0 && activityData.station_id) {
        // Se não tiver stations_field, usar a station_id da atividade
        const activityStation = userStations.find(s => s.id === activityData.station_id);
        if (activityStation) {
          rawStations = [{ id: activityStation.id, title: activityStation.name }];
        }
      }
      
      // Enriquecer stations com o nome correto
      const stationsField = Array.isArray(rawStations) ? rawStations.map(station => {
        const stationId = typeof station === 'object' ? station.id : station;
        const userStation = userStations.find(s => s.id === stationId);
        return { 
          id: stationId, 
          title: userStation?.name || userStation?.title || station?.title || station?.name || '' 
        };
      }) : [];

      // Criar formData - remover categories_field e stations_field do parsedBody para evitar sobrescrita
      const { categories_field: _, stations_field: __, ...restParsedBody } = parsedBody;

      // Segment_users: converter IDs para {value, label} ANTES de inicializar o form
      let resolvedSegmentUsers = [];
      const targetObj = activityData.target || parsedBody.target;
      if (targetObj && targetObj.segment_users && Array.isArray(targetObj.segment_users) && targetObj.segment_users.length > 0) {
        const plainIds = targetObj.segment_users.map(u => parseInt(u.value || u.id || u, 10)).filter(id => !isNaN(id));
        if (plainIds.length > 0) {
          try {
            const resp = await axios.get(`${BASE_API}/users/users-list/${plainIds.join(',')}`, { headers: { Authorization: `${token}` } });
            resolvedSegmentUsers = (resp.data || []).map(u => ({ value: u.id, label: `${u.name} - ${u.email}` }));
          } catch (e) { console.error('Erro ao buscar segment_users:', e); }
        }
      }
      
      const formData = {
        id: activityData.id,
        ...restParsedBody,
        body_name: activityData.name || parsedBody.name,
        body_description: activityData.description || parsedBody.description,
        body_summary: activityData.summary || parsedBody.summary,
        image: activityData.image || parsedBody.image,
        status: activityData.status,
        categories_field: categoriesField,
        groupings_field: activityData.groupings || parsedBody.groupings || [],
        stations_field: stationsField,
        partners_split: parsedBody.partners_split || [],
        products: (() => {
          // Mapear produtos do banco para o formato do form
          const rawProducts = activityData.products || parsedBody.products || [];
          return rawProducts.map(p => ({
            ...p,
            price: p.price !== undefined ? p.price : (p.value || 0),
            discount: p.discount !== undefined ? p.discount : (p.discount_value || 0),
            stock: p.stock || null,
            limit_per_user: p.limit_per_user !== undefined ? p.limit_per_user : (p.limit || null),
          }));
        })(),
        station_images: parsedBody.station_images || {},
        // Datas
        start: activityData.start,
        end: activityData.end,
        participationStart: activityData.participationStart,
        participationEnd: activityData.participationEnd,
        // Configurações
        rules: activityData.rules || parsedBody.rules,
        limit: activityData.limit || parsedBody.limit || '0',
        limitPerUser: activityData.limitPerUser || parsedBody.limitPerUser,
        // Switches e flags
        priority: activityData.priority || parsedBody.priority || false,
        priorityCarousel: activityData.priorityCarousel || parsedBody.priorityCarousel || false,
        has_target: activityData.has_target !== undefined ? activityData.has_target : true,
        only_public: activityData.only_public || parsedBody.only_public || false,
        featuredPublic: activityData.featuredPublic || parsedBody.featuredPublic || false,
        featuredPrivate: activityData.featuredPrivate || parsedBody.featuredPrivate || false,
        comments_and_likes_enabled: activityData.comments_and_likes_enabled !== undefined 
          ? activityData.comments_and_likes_enabled 
          : true,
        // Target (público alvo) e segmentação
        // O backend salva segment_users dentro de target: { segment_users: [1,2,3] }
        // e segmentation_type não é persistido — Tem que inferir do conteúdo do target
        target: activityData.target || parsedBody.target,
        segmentation_type: (() => {
          const t = activityData.target || parsedBody.target;
          if (t && t.segment_users && Array.isArray(t.segment_users) && t.segment_users.length > 0) return 'users';
          return activityData.segmentation_type || parsedBody.segmentation_type || 'public';
        })(),
        from: activityData.target?.age?.from || activityData.target?.from || parsedBody.target?.age?.from || parsedBody.from,
        to: activityData.target?.age?.to || activityData.target?.to || parsedBody.target?.age?.to || parsedBody.to,
        states: activityData.target?.states || activityData.states || parsedBody.states || [],
        segment_users: resolvedSegmentUsers,
        // Pagamento
        payment_option: activityData.payment_option || parsedBody.payment_option || 'payment_only',
        allow_installments: activityData.allow_installments || parsedBody.allow_installments || false,
        max_installments: activityData.max_installments || parsedBody.installments_count || 12,
        free_installments: activityData.free_installments || parsedBody.installments_free || 0,
        // Tipo de venda
        recurrence_type: parsedBody.recurrence_type || 'single',
      };

      if (props.initialize) {
        props.initialize(formData);
      }
      
      setSelectedActivity(activity);
      setValue(0);
      setOpen(true);
    } catch (error) {
      console.error('Erro ao carregar dados da atividade:', error);
      alert('Erro ao carregar atividade: ' + error.message);
    }
  }, [props]);

  const handleViewResults = useCallback((activity) => {
    setSelectedActivity(activity);
    setResultsModalOpen(true);
  }, []);

  const handleEndActivity = useCallback(async (activity) => {
    const activityId = activity?.id || activity;
    if (isMultiStationChildAction(activity) || isMultiStationChildAction(activity?.rawData)) {
      alert('Esta atividade foi publicada pela entidade pai e só pode ser encerrada lá.');
      return;
    }
    if (!window.confirm('Tem certeza que deseja encerrar esta atividade?')) {
      return;
    }
    
    try {
      const token = JSON.parse(localStorage.getItem(USER_TOKEN));
      await axios.put(
        `${BASE_API}/archive-action/${activityId}`,
        {},
        { headers: { Authorization: `${token}` } }
      );
      alert('Atividade encerrada com sucesso!');
      fetchActivities();
    } catch (error) {
      console.error('Erro ao encerrar atividade:', error);
      alert(error.response?.data?.messages?.[0] || 'Erro ao encerrar atividade. Tente novamente.');
    }
  }, [fetchActivities]);

  const handleShareActivity = useCallback((activity) => {
    setShareActivity(activity);
    setShareModalOpen(true);
  }, []);

  const handleDuplicateActivity = useCallback(async (activity) => {
    if (isMultiStationChildAction(activity) || isMultiStationChildAction(activity?.rawData)) {
      alert(CHILD_ACTION_EDIT_BLOCKED_MESSAGE);
      return;
    }
    try {
      const token = JSON.parse(localStorage.getItem(USER_TOKEN));
      const response = await axios.get(`${BASE_API}/actions/${activity.id}`, {
        headers: { Authorization: `${token}` },
      });
      
      const activityData = response.data;
      let parsedBody = {};
      // Dados ficam em station_specific_data (coluna 'body' não existe na tabela Actions)
      const rawConfig = activityData.station_specific_data || activityData.body;
      if (rawConfig) {
        try {
          parsedBody = typeof rawConfig === 'string' ? JSON.parse(rawConfig) : rawConfig;
        } catch (e) {
          console.warn('Erro ao fazer parse do station_specific_data:', e);
        }
      }

      const formData = {
        ...parsedBody,
        ...activityData,
        id: undefined, // Remove ID para criar nova
        body_name: `${activityData.name || parsedBody.name} (Cópia)`,
        body_description: activityData.description || parsedBody.description,
        body_summary: activityData.summary || parsedBody.summary,
        categories_field: activityData.categories || parsedBody.categories || [],
        groupings_field: activityData.groupings || parsedBody.groupings || [],
        stations_field: parsedBody.stations_field || [],
        partners_split: parsedBody.partners_split || [],
        products: parsedBody.products || activityData.products || [],
      };

      if (props.initialize) {
        props.initialize(formData);
      }
      
      setSelectedActivity(null);
      setValue(0);
      setOpen(true);
    } catch (error) {
      console.error('Erro ao duplicar atividade:', error);
    }
  }, [props]);

  // Detectar rota de edição via URL PRIMEIRO (antes de fetchActivities)
  useEffect(() => {
    const { params, location } = props;
    const pathname = location?.pathname || '';
    
    // Verificar se é rota de edição
    let activityId = null;
    let isEditMode = false;
    
    if (params && params.id && params.action === 'editar') {
      activityId = parseInt(params.id, 10);
      isEditMode = true;
    } else if (pathname) {
      const match = pathname.match(/vendas-online-parceiro\/(\d+)\/editar/);
      if (match) {
        activityId = parseInt(match[1], 10);
        isEditMode = true;
      }
    }
    
    // Se é modo de edição, carregar atividade imediatamente
    if (isEditMode && activityId && loadedActivityIdRef.current !== activityId && !open) {
      loadedActivityIdRef.current = activityId;
      loadActivityForEditFromUrl(activityId);
    } else if (!isEditMode) {
      // Só buscar lista de atividades se NÃO for modo de edição
      fetchActivities();
    }
    
    props.getLottiesList();
    props.getPartners(stationId);
  }, []);

  useEffect(() => {
    // Só buscar atividades se não estiver em modo de edição
    const { params, location } = props;
    const pathname = location?.pathname || '';
    const isEditMode = (params && params.id && params.action === 'editar') || 
                       pathname.match(/vendas-online-parceiro\/(\d+)\/editar/);
    
    if (!isEditMode) {
      fetchActivities();
    }
  }, [tablePage, shownPerPage]);

  // Função para carregar atividade via URL de edição
  const loadActivityForEditFromUrl = async (activityId) => {
    try {
      const token = JSON.parse(localStorage.getItem(USER_TOKEN));
      const response = await axios.get(`${BASE_API}/actions/${activityId}`, {
        headers: { Authorization: `${token}` },
      });
      
      const activityData = response.data;
      
      let parsedBody = {};
      const rawUrlConfig = activityData.station_specific_data || activityData.body;
      if (rawUrlConfig) {
        try {
          parsedBody = typeof rawUrlConfig === 'string' ? JSON.parse(rawUrlConfig) : rawUrlConfig;
        } catch (e) {
          console.warn('Erro ao fazer parse do station_specific_data:', e);
        }
      }

      let rawCategories = [];
      if (activityData.category_actions && Array.isArray(activityData.category_actions) && activityData.category_actions.length > 0) {
        rawCategories = activityData.category_actions.map(ca => ca.category || ca);
      } else if (parsedBody.categories_field && Array.isArray(parsedBody.categories_field)) {
        rawCategories = parsedBody.categories_field;
      }
      
      const categoriesField = rawCategories.map(cat => {
        if (typeof cat === 'object' && cat !== null) {
          return { id: cat.id, title: cat.title || cat.name || '' };
        }
        return { id: cat, title: '' };
      });

      const userStations = [];
      const establishments = props.auth?.user?.establishments || [];
      establishments.forEach(est => {
        if (est.stations && Array.isArray(est.stations)) {
          est.stations.forEach(s => {
            if (!userStations.find(us => us.id === s.id)) {
              userStations.push(s);
            }
          });
        }
      });
      
      let rawStations = parsedBody.stations_field || [];
      if (rawStations.length === 0 && activityData.station_id) {
        const activityStation = userStations.find(s => s.id === activityData.station_id);
        if (activityStation) {
          rawStations = [{ id: activityStation.id, title: activityStation.name }];
        }
      }
      
      const stationsField = Array.isArray(rawStations) ? rawStations.map(station => {
        const stationId = typeof station === 'object' ? station.id : station;
        const userStation = userStations.find(s => s.id === stationId);
        return { 
          id: stationId, 
          title: userStation?.name || userStation?.title || station?.title || station?.name || '' 
        };
      }) : [];

      const { categories_field: _, stations_field: __, ...restParsedBody } = parsedBody;

      // Segment_users: converter para {value, label} ANTES de inicializar o form
      let resolvedSegmentUsers = [];
      const targetObj = activityData.target || parsedBody.target;
      if (targetObj && targetObj.segment_users && Array.isArray(targetObj.segment_users) && targetObj.segment_users.length > 0) {
        const plainIds = targetObj.segment_users.map(u => parseInt(u.value || u.id || u, 10)).filter(id => !isNaN(id));
        if (plainIds.length > 0) {
          try {
            const resp = await axios.get(`${BASE_API}/users/users-list/${plainIds.join(',')}`, { headers: { Authorization: `${token}` } });
            resolvedSegmentUsers = (resp.data || []).map(u => ({ value: u.id, label: `${u.name} - ${u.email}` }));
          } catch (e) { console.error('Erro ao buscar segment_users:', e); }
        }
      }

      const formData = {
        id: activityData.id,
        ...restParsedBody,
        body_name: activityData.name || parsedBody.name,
        body_description: activityData.description || parsedBody.description,
        body_summary: activityData.summary || parsedBody.summary,
        image: activityData.image || parsedBody.image,
        status: activityData.status,
        categories_field: categoriesField,
        stations_field: stationsField,
        products: (() => {
          const rawProducts = activityData.products || parsedBody.products || [];
          return rawProducts.map(p => ({
            ...p,
            price: p.price !== undefined ? p.price : (p.value || 0),
            discount: p.discount !== undefined ? p.discount : (p.discount_value || 0),
            stock: p.stock || null,
            limit_per_user: p.limit_per_user !== undefined ? p.limit_per_user : (p.limit || null),
          }));
        })(),
        start: activityData.start,
        end: activityData.end,
        participationStart: activityData.participationStart,
        participationEnd: activityData.participationEnd,
        rules: activityData.rules || parsedBody.rules,
        limit: activityData.limit || parsedBody.limit || '0',
        limitPerUser: activityData.limitPerUser || parsedBody.limitPerUser,
        priority: activityData.priority || false,
        priorityCarousel: activityData.priorityCarousel || false,
        has_target: activityData.has_target !== undefined ? activityData.has_target : true,
        only_public: activityData.only_public || parsedBody.only_public || false,
        featuredPublic: activityData.featuredPublic || parsedBody.featuredPublic || false,
        featuredPrivate: activityData.featuredPrivate || parsedBody.featuredPrivate || false,
        comments_and_likes_enabled: activityData.comments_and_likes_enabled !== undefined
          ? activityData.comments_and_likes_enabled
          : true,
        target: activityData.target || parsedBody.target,
        segmentation_type: (() => {
          const t = activityData.target || parsedBody.target;
          if (t && t.segment_users && Array.isArray(t.segment_users) && t.segment_users.length > 0) return 'users';
          return activityData.segmentation_type || parsedBody.segmentation_type || 'public';
        })(),
        from: activityData.target?.age?.from || activityData.target?.from || parsedBody.target?.age?.from || parsedBody.from,
        to: activityData.target?.age?.to || activityData.target?.to || parsedBody.target?.age?.to || parsedBody.to,
        states: activityData.target?.states || activityData.states || parsedBody.states || [],
        segment_users: resolvedSegmentUsers,
        payment_option: activityData.payment_option || parsedBody.payment_option || 'payment_only',
        allow_installments: activityData.allow_installments || parsedBody.allow_installments || false,
        max_installments: activityData.max_installments || parsedBody.installments_count || 12,
        free_installments: activityData.free_installments || parsedBody.installments_free || 0,
        recurrence_type: parsedBody.recurrence_type || 'single',
      };

      if (props.initialize) {
        props.initialize(formData);
      }
      
      setSelectedActivity(activityData);
      setValue(0);
      setOpen(true);
    } catch (error) {
      console.error('Erro ao carregar atividade para edição:', error);
    }
  };

  function goBack() {
    if (value > 0) {
      setValue(value - 1);
    }
  }

  function handleOpenConfirmation() {
    setOpenConfirmation(true);
  }

  function handleCancelClose() {
    setOpenConfirmation(false);
  }

  // Navegação entre abas pelas setas
  function handleNativeChange(e, newValue) {
    // Permite navegar livremente em modo edição
    // Em modo criação, só permite voltar (não pular etapas)
    if (selectedActivity) {
      setValue(newValue);
    } else {
      if (newValue < value) {
        setValue(newValue);
      }
    }
  }

  // Avança para a próxima aba (chamado pelo botão "Salvar e continuar")
  function onClickProgress() {
    if (value < 3) {
      setValue(value + 1);
    }
  }

  // Função para salvar/criar a atividade (chamada na última aba)
  async function handleSaveActivity(values) {
    try {
      const token = JSON.parse(localStorage.getItem(USER_TOKEN));
      
      const payload = {
        ...values,
        type_id: VENDAS_ONLINE_PARCEIRO_TYPE_ID,
        name: values.body_name,
        description: values.body_description,
        summary: values.body_summary,
        station_id: stationId,
        body: JSON.stringify({
          ...values,
          name: values.body_name,
          description: values.body_description,
          summary: values.body_summary,
        }),
      };

      if (selectedActivity?.id) {
        await axios.put(`${BASE_API}/actions/${selectedActivity.id}`, payload, {
          headers: { Authorization: `${token}` },
        });
      } else {
        await axios.post(`${BASE_API}/actions`, payload, {
          headers: { Authorization: `${token}` },
        });
      }

      setOpen(false);
      fetchActivities();
      props.dispatchFormReset();
    } catch (error) {
      console.error('Erro ao salvar atividade:', error);
    }
  }

  // Fecha o modal com confirmação
  function closeModal() {
    setOpenConfirmation(false);
    setOpen(false);
    props.dispatchFormReset();
    loadedActivityIdRef.current = null; // Resetar ref para permitir reabrir
    
    // Se acessou via URL de edição, navegar de volta para a lista
    if (props.location?.pathname?.includes('/editar')) {
      props.router.push('/owner/acoes/minhas-atividades?from=online');
    }
  }

  const columns = [
    {
      field: 'name',
      title: 'Nome',
      render: (rowData) => (
        <ActionNameElement
          name={rowData.name}
          image={rowData.image}
        />
      ),
    },
    { field: 'status', title: 'Status' },
    { field: 'createdAt', title: 'Data de Criação' },
    { field: 'participations', title: 'Vendas' },
    {
      field: 'revenue',
      title: 'Receita',
      render: (rowData) => `R$ ${(rowData.revenue || 0).toFixed(2).replace('.', ',')}`,
    },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <LoadingProgress />
      </Box>
    );
  }

  return (
    <section id="vendas-online-parceiro">
      <GeneralGrid container spacing={3}>
        <GeneralGrid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Typography variant="h5" style={{ fontWeight: 'bold' }}>
              Vendas Online com Parceiro
            </Typography>
            <PrimaryButton
              onClick={handleOpen}
              startIcon={<AddIcon />}
            >
              Nova Atividade
            </PrimaryButton>
          </Box>
        </GeneralGrid>

        <GeneralGrid item xs={12}>
          <Table
            columns={columns}
            data={data || []}
            title=""
            toolbarSearch
            actions={[
              {
                action: (rowData) =>
                  isMultiStationChildAction(rowData)
                    ? null
                    : {
                        icon: () => <EditIcon style={{ color: appColors.primary }} />,
                        tooltip: 'Editar',
                        onClick: (event, data) => handleEditActivity(data),
                      },
              },
              {
                icon: () => <BarChartIcon style={{ color: appColors.primary }} />,
                tooltip: 'Ver Resultados',
                onClick: (event, rowData) => handleViewResults(rowData),
              },
              {
                icon: () => <ShareIcon style={{ color: appColors.primary }} />,
                tooltip: 'Divulgar',
                onClick: (event, rowData) => handleShareActivity(rowData),
              },
              {
                action: (rowData) =>
                  isMultiStationChildAction(rowData)
                    ? null
                    : {
                        icon: () => <FileCopyIcon style={{ color: appColors.primary }} />,
                        tooltip: 'Duplicar',
                        onClick: (event, data) => handleDuplicateActivity(data),
                      },
              },
              {
                action: (rowData) =>
                  isMultiStationChildAction(rowData)
                    ? null
                    : {
                        icon: () => <StopIcon style={{ color: appColors.error }} />,
                        tooltip: 'Encerrar',
                        onClick: (event, data) => handleEndActivity(data),
                      },
              },
            ]}
          />
        </GeneralGrid>
      </GeneralGrid>

      {/* Modal de Criação/Edição */}
      <VendasOnlineParceiroModal
        visible={open}
        onClose={handleOpenConfirmation}
        height="90vh"
        value={value}
        goBack={goBack}
        handleNativeChange={handleNativeChange}
        handleOpenConfirmation={handleOpenConfirmation}
        handleCancelClose={handleCancelClose}
        openConfirmation={openConfirmation}
        setOpenConfirmation={setOpenConfirmation}
        closeModal={closeModal}
        onClickProgress={onClickProgress}
        handleSaveActivity={handleSaveActivity}
        router={props.router}
        location={props.location}
        editMode={!!selectedActivity}
        activityId={selectedActivity?.id}
      />

      {/* Modal de Divulgação */}
      {shareModalOpen && shareActivity && (
        <ShareModal
          open={shareModalOpen}
          onClose={() => {
            setShareModalOpen(false);
            setShareActivity(null);
          }}
          activity={shareActivity}
        />
      )}

      {/* Modal de Resultados */}
      {resultsModalOpen && selectedActivity && (
        <ResultsModal
          open={resultsModalOpen}
          onClose={() => setResultsModalOpen(false)}
          activity={selectedActivity}
        />
      )}
    </section>
  );
}

Main.propTypes = {
  auth: PropTypes.object.isRequired,
  router: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  params: PropTypes.object,
  formValues: PropTypes.object,
  initialize: PropTypes.func,
  dispatchFormReset: PropTypes.func.isRequired,
  getLottiesList: PropTypes.func.isRequired,
  getPartners: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  formValues: getFormValues('vendasOnlineParceiroForm')(state),
  vendasOnlineParceiro: state.vendasOnlineParceiro,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      getList,
      getDetail,
      dispatchFormReset,
      getLottiesList,
      getPartners,
      initialize: (data) => initialize('vendasOnlineParceiroForm', data),
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(Main);
